/**
 * Czech message catalog — mirrors the shape of `messages.en.ts` exactly;
 * `satisfies MessagesShape` makes a missing or renamed key a TypeScript
 * error. Register: vy (vykání), formal (locked in l10n/locales.md — the one
 * formal Slavic locale in this wave).
 *
 * Platform terms follow Czech UI conventions (nastavení, párování for
 * pairing, zrcadlení for notification mirroring, smazat for dismiss,
 * konverzace for conversation). "Glance" is the untranslated feature brand
 * name (glossary D1); „klip“ renders the D2 loanword in Czech script.
 *
 * Plural notes: Czech is a {one, few, many, other} CLDR locale — every
 * count-bearing `selectPlural("cs", …)` supplies all four forms. one = 1,
 * few = 2–4, many = fractional quantities only (1,5 — genitive-singular
 * noun form), other = 0 and 5+. All four always format the number.
 *
 * Out of scope: the same exclusions as the English catalog (log scopes,
 * structured event names, JSDoc, server-emitted error messages — see
 * `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Připojeno",
    statusReconnecting: "Opětovné připojování…",
    statusDisconnected: "Odpojeno",
    openSettings: "Otevřít nastavení",
    pairedFallbackDevice: "váš telefon",
    publicModeToggleEnable: "Skrýt obsah oznámení",
    publicModeToggleDisable: "Zobrazit obsah oznámení",
  },

  notifications: {
    replyAriaLabel: "Odpovědět",
    replyPlaceholder: "Odpovědět…",
    replySend: "Odeslat",
    sent: "Odesláno",
    failedToSend: "Odeslání se nezdařilo",
    unread: "Nepřečteno",
    dismiss: "Smazat",
    notificationImageAlt: "Obrázek z oznámení",
  },

  conversations: {
    replyAriaLabel: "Odpovědět",
    replyPlaceholder: "Odpovědět…",
    replySend: "Odeslat",
    sent: "Odesláno",
    failedToSend: "Odeslání se nezdařilo",
    dismiss: "Smazat konverzaci",
    messagesListLabel: "Zprávy v této konverzaci",
    deviceOwner: "Vy",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Žádné nepřečtené zprávy"
        : `${count} ${selectPlural("cs", count, {
            one: "nepřečtená zpráva",
            few: "nepřečtené zprávy",
            many: "nepřečtené zprávy",
            other: "nepřečtených zpráv",
          })}`,
  },

  list: {
    today: "Dnes",
    yesterday: "Včera",
    searchNotifications: "Hledat oznámení",
    clearSearch: "Vymazat hledání",
    clearAll: "Smazat vše",
    markAllRead: "Označit vše jako přečtené",
    groupCheckboxLabel: "Seskupit",
    noNotificationsTitle: "Zatím žádná oznámení",
    noNotificationsDesc: "Nové zprávy zrcadlené z vašeho telefonu se zobrazí zde.",
    noMatchesTitle: "Nic nenalezeno",
    notificationsList: "Oznámení",
    privacyAppList: "Oznámení podle aplikací",
    clear: "Smazat",
    clearMatches: (count: number): string =>
      `Smazat ${count} ${selectPlural("cs", count, {
        one: "shodu",
        few: "shody",
        many: "shody",
        other: "shod",
      })}`,
    noMatchesDesc: (query: string): string => `Žádná oznámení neodpovídají „${query}“.`,
    appNotificationsGroup: (appName: string): string => `Oznámení ${appName}`,
    dismissAllFrom: (appName: string): string => `Smazat vše od ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("cs", count, {
              one: "zpráva",
              few: "zprávy",
              many: "zprávy",
              other: "zpráv",
            })
          : kind === "calls"
            ? selectPlural("cs", count, {
                one: "hovor",
                few: "hovory",
                many: "hovoru",
                other: "hovorů",
              })
            : selectPlural("cs", count, {
                one: "oznámení",
                few: "oznámení",
                many: "oznámení",
                other: "oznámení",
              });
      return `${count} ${noun}`;
    },
    kbdDismiss: "smazat",
    kbdNavigate: "navigace",
    kbdOrSeparator: "nebo",
  },

  settings: {
    back: "Zpět na oznámení",
    settings: "Nastavení",
    appearance: "Vzhled",
    theme: "Motiv",
    themeAuto: "Automatický",
    themeLight: "Světlý",
    themeDark: "Tmavý",
    language: "Jazyk",
    languageSystemDefault: "Podle systému",
    notificationsSection: "Oznámení",
    osNotifications: "Systémová oznámení",
    callNotifications: "Oznámení o hovorech",
    privacySection: "Soukromí",
    analyticsToggle: "Anonymní statistiky používání",
    analyticsToggleDescription: "Pouze počty a milníky — nikdy vaše oznámení.",
    connection: "Připojení",
    status: "Stav",
    statusConnected: "Připojeno",
    statusReconnecting: "Opětovné připojování…",
    statusDisconnected: "Odpojeno",
    disconnectFromPhone: "Odpojit od telefonu",
    disconnectConfirm: "Opravdu? Tím zrušíte párování zařízení.",
    yesDisconnect: "Ano, odpojit",
    cancel: "Zrušit",
    aboutAndSupport: "O aplikaci a podpora",
    version: "Verze",
    encryption: "Šifrování",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Potřebujete pomoc? Napište na __EMAIL__",
    disconnectFailed: "Odpojení se nezdařilo — zkuste to prosím znovu",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Odpojeno, ale nepodařilo se vyčistit místní data: ${errorMsg}. Načtěte stránku znovu.`,
    disconnectLocalCleanupFailed:
      "Odpojeno, ale nepodařilo se vyčistit místní data. Načtěte stránku znovu.",
  },

  pairing: {
    enterCodeInstruction: "Zadejte tento kód v aplikaci Reflecto",
    pairedSuccessfully: "Úspěšně spárováno!",
    pairingFailed: "Párování se nezdařilo",
    requestingPairingCode: "Získávání párovacího kódu…",
    codeExpired: "Platnost kódu vypršela",
    tryAgain: "Zkusit znovu",
    failedToStartPairing: "Nepodařilo se zahájit párování",
    needAppText: "Potřebujete aplikaci pro Android? Napište na __EMAIL__ a získáte přístup.",
  },

  dnd: {
    title: "Nerušit",
    description: "Pozastavit oznámení na tomto zařízení na příští hodinu.",
    snooze1h: "Pozastavit na 1 hodinu",
    snoozeActiveUntil: (formattedTime: string): string => `Aktivní do ${formattedTime}`,
    cancelSnooze: "Zrušit",
  },

  connectors: {
    title: "Připojené služby",
    emptyBody:
      "Zatím žádné připojené služby. Připojte ChatGPT nebo Claude v nastavení jejich konektoru a poté potvrďte kód v telefonu.",
    scopeSend: "Může odesílat oznámení",
    added: (date: string): string => `Přidáno ${date}`,
    lastUsed: (relative: string): string => `Naposledy použito ${relative}`,
    neverUsed: "Nikdy nepoužito",
    revoke: "Odpojit",
    revokeConfirm: (label: string): string => `Odpojit ${label}? Okamžitě ztratí přístup.`,
    revokeAction: "Odpojit",
    revokeConfirmLabel: (label: string): string => `Odpojit ${label}`,
    cancel: "Zrušit",
    loadError: "Nepodařilo se načíst připojené služby. Zkuste to prosím znovu.",
    revokeError: "Nepodařilo se odpojit službu. Zkuste to prosím znovu.",
    retry: "Opakovat",
  },

  feedback: {
    title: "Odeslat zpětnou vazbu",
    close: "Zavřít",
    messageLabel: "Vaše zpráva",
    messagePlaceholder: "Našli jste chybu, máte nápad, nebo vám něco nesedí? Napište nám.",
    categoryLabel: "Kategorie",
    categoryBug: "Chyba",
    categoryIdea: "Nápad",
    categoryOther: "Jiné",
    emailLabel: "E-mail (nepovinné, jen pokud chcete odpověď)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Nezařadíme vás do žádného seznamu.",
    disclosure:
      "Vaše zpráva se odesílá na server Reflecto a ukládá se v našem interním systému pro sledování úkolů. Přikládáme verzi aplikace a identifikátor zařízení přidělený při párování, abychom mohli hlášení propojit. Nevidíme vaše oznámení, kontakty ani žádné osobní údaje. E-mail je nepovinný — uveďte ho, jen pokud chcete odpověď. Nezařadíme vás do žádného seznamu.",
    submit: "Odeslat zpětnou vazbu",
    sending: "Odesílání…",
    success: "Děkujeme — přečte to člověk, obvykle do 24 hodin",
    error: "Odeslání se nezdařilo. Zkuste to prosím znovu.",
    done: "Hotovo",
    sendFeedback: "Odeslat zpětnou vazbu",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Zavřít",
    aiSummaryTag: "Souhrn od AI",
    errorAi: "Souhrn se teď nepodařilo vytvořit.",
    retryCta: "Zkusit znovu",
    downloadHeading: "Souhrny od AI",
    downloadBody:
      "Doháníte, co vám uniklo, díky souhrnu od AI přímo v zařízení. Vše běží výhradně ve vašem zařízení.",
    downloadCta: "Stáhnout model",
    downloadPrivacyNote: "Jednorázové stažení. Server nikdy nevidí vaše oznámení.",
    downloadingHeading: "Stahování modelu…",
    caughtUp: "Máte vše přečtené — teď nic nevyžaduje vaši pozornost.",
    needsYou: "Vyžaduje pozornost",
    quiet: "Ostatní",
    scanning: (pending: number): string => `Připravuje se souhrn: ${pending}…`,
    jump: "Otevřít oznámení",
    affordanceReply: "Odpovědět",
    affordanceOpen: "Otevřít",
    affordanceCopyOtp: "Kopírovat kód",
    buttonLabel: "Glance",
  },

  update: {
    title: "Dostupná aktualizace",
    description: "Je připravena nová verze aplikace Reflecto. Aktualizujte ji načtením stránky.",
    reload: "Načíst znovu",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Toto zařízení jsme pojmenovali „${name}“.`,
    cardLooksGood: "Vypadá dobře",
    cardEdit: "Upravit",
    cardAriaLabel: "Potvrďte název zařízení",
    renameTitle: "Přejmenovat toto zařízení",
    renameLabel: "Název zařízení",
    renameSave: "Uložit",
    renameCancel: "Zrušit",
    renameFailed: "Přejmenování se nezdařilo. Zkuste to prosím znovu.",
  },

  collision: {
    title: (name: string): string => `Už máte „${name}“`,
    subtitle: "Jak toto zařízení pojmenujeme?",
    chipWork: (name: string): string => `Pracovní ${name}`,
    chipHome: (name: string): string => `Domácí ${name}`,
    chipPersonal: (name: string): string => `Osobní ${name}`,
    customLabel: "Vlastní název",
    save: "Uložit",
    useAnyway: "Přesto použít původní název",
    saveFailed: "Nový název se nepodařilo uložit. Zkuste to prosím znovu.",
    ariaLabel: "Vyřešte shodu názvů zařízení",
  },

  clipboard: {
    title: "Schránka",
    empty: "Klipy z vašich ostatních zařízení se zobrazí zde. Klepnutím zkopírujete.",
    from: (label: string): string => `od ${label}`,
    copy: "Kopírovat do schránky",
    copied: "Zkopírováno",
    reveal: "Zobrazit",
    hide: "Skrýt",
    sensitiveChip: "Citlivé",
    cloudChip: "přes váš token API",
    maskedHint: "Obsah je skrytý — stiskněte Zobrazit",
    sendPlaceholder: "Vložte text k odeslání…",
    sendCta: "Odeslat",
    sendSensitiveToggle: "Označit jako citlivé",
    sendSuccess: (n: number): string =>
      `Odesláno na ${n} ${selectPlural("cs", n, {
        one: "zařízení",
        few: "zařízení",
        many: "zařízení",
        other: "zařízení",
      })}.`,
    sendPartial: (n: number, total: number): string =>
      `Odesláno na ${n} z ${total} ${selectPlural("cs", total, {
        one: "zařízení",
        few: "zařízení",
        many: "zařízení",
        other: "zařízení",
      })}.`,
    sendNoDevices: "Žádná spárovaná zařízení k odeslání.",
    sendFailed: "Odeslání se nezdařilo. Zkuste to prosím znovu.",
    sendEmpty: "Není co odeslat.",
    dockExpand: "Zobrazit historii schránky",
    dockCollapse: "Sbalit",
    dockSendTitle: "Odeslat na vaše zařízení",
    dockFromClipboard: (chars: number): string =>
      `Ze schránky · ${chars} ${selectPlural("cs", chars, {
        one: "znak",
        few: "znaky",
        many: "znaku",
        other: "znaků",
      })}`,
    dockSend: (n: number): string => `Odeslat na ${n}`,
    dockCancel: "Zrušit",
    dockRecentHeading: "Nedávné",
    dockEmptyClipboard: "Vaše schránka je prázdná.",
    shareTitle: "Odeslat na vaše zařízení",
    shareReview:
      "Zkontrolujte, co odesíláte. Data poputují na vaše spárovaná zařízení se šifrováním end-to-end.",
    shareSend: "Odeslat",
    shareCancel: "Zrušit",
    shareNotPaired: "Chcete-li odeslat sdílený text, nejprve spárujte zařízení.",
    shareOpenApp: "Otevřít Reflecto",
  },
  install: {
    title: "Nainstalovat Reflecto",
    body: "Přidejte na plochu pro rychlejší přístup.",
    cta: "Nainstalovat",
    dismissAria: "Zavřít výzvu k instalaci",
  },
  common: {
    loading: "Načítání",
  },
  errors: {
    notPaired: "Nespárováno",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} na ${os}`,
    webFallback: "Webový prohlížeč",
  },

  palette: {
    placeholder: "Zadejte příkaz…",
    noResults: "Žádné odpovídající příkazy",
    ariaLabel: "Paleta příkazů",
    clearAll: "Smazat všechna oznámení",
    glance: "Souhrn Glance",
    cancelSnooze: "Zrušit pozastavení",
    copyClip: "Kopírovat poslední přijatý klip",
  },
} satisfies MessagesShape;
